import { useMutation, useQueryClient } from "@tanstack/react-query";

import { workbenchWorknumberApi } from "../../../../../api/client";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { extractDataStatus, mapHistory } from "./worknumber.mapping";
import type { PullWorknumberResult, WorkNumberHistory } from "./worknumber.types";
import { worknumberKeys } from "./worknumberKeys";

/** Input for a Work Number pull — the applicant to pull work history for. */
export interface PullWorknumberInput {
  approvalId: string;
  applicantId: string;
}

/**
 * Mutation hook for pulling purchased employer info (the button click). Backed by
 * WorkbenchWorknumberApi.pullWorkNumber (POST), which pulls + persists the
 * applicant's income verification at recap-service. A per-attempt
 * `Idempotency-Key` dedups accidental double-submits. On success the mapped
 * history is written straight into the history query cache (no extra fetch), so
 * the results table renders immediately. The response also carries
 * `X-Recalc-Deferred` / `X-Recalc-Deferred-Reason` headers (axios lowercases
 * header names) when the paired stipulation-service recalc didn't complete;
 * callers use `recalcDeferred` to surface the "Update Employer Stips" retry.
 * The response's HTTP status and recap's business-level `data.status` (e.g. a
 * 202 with `"NOTFOUND"`) are also surfaced via `httpStatus` / `dataStatus` so
 * callers can toast a warning instead of silently showing an
 * empty table when recap didn't return a 200 with rows.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function usePullWorknumber() {
  const queryClient = useQueryClient();
  const shouldForceExit = useForceExitStore((state) => state.shouldForceExit);

  return useMutation({
    mutationFn: async ({ approvalId, applicantId }: PullWorknumberInput): Promise<PullWorknumberResult> => {
      if (shouldForceExit) {
        return { history: {} as WorkNumberHistory, recalcDeferred: false, httpStatus: 200 };
      }
      const idempotencyKey = globalThis.crypto?.randomUUID?.();
      const response = await workbenchWorknumberApi.pullWorkNumber(approvalId, applicantId, idempotencyKey);
      const recalcDeferredReason = response.headers["x-recalc-deferred-reason"] as string | undefined;
      return {
        history: mapHistory(response.data),
        recalcDeferred: Boolean(response.headers["x-recalc-deferred"] || recalcDeferredReason),
        recalcDeferredReason,
        httpStatus: response.status,
        dataStatus: extractDataStatus(response.data),
      };
    },
    onSuccess: ({ history }, { approvalId, applicantId }) => {
      queryClient.setQueryData<WorkNumberHistory>(worknumberKeys.history({ approvalId, applicantId }), history);
    },
  });
}
